import {useState} from 'react';

import {Box} from '@mui/material';

// Vertical resize handle that sits between two columns in a grid layout.
// Render as its own grid column (width 6) so it doesn't overlap content.
// The interactive hit-zone is wider than the visible line via a hover
// halo, while the drag affordance itself stays subtle until hovered or
// actively dragged.
export default function ResizeHandle({
    onStartDrag,
    color,
    ariaLabel = 'Resize panel',
    className = '',
}) {
    const [hovered, setHovered] = useState(false);
    const [active, setActive] = useState(false);

    const handlePointerDown = (event) => {
        setActive(true);
        onStartDrag?.(event);
        const stop = () => {
            setActive(false);
            window.removeEventListener('mouseup', stop);
            window.removeEventListener('touchend', stop);
            window.removeEventListener('touchcancel', stop);
        };
        window.addEventListener('mouseup', stop);
        window.addEventListener('touchend', stop);
        window.addEventListener('touchcancel', stop);
    };

    const visible = hovered || active;
    const lineColor = color || '#888';

    return (
        <Box
            role="separator"
            aria-orientation="vertical"
            aria-label={ariaLabel}
            tabIndex={-1}
            className={className}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            sx={{
                position: 'relative',
                width: '6px',
                cursor: 'col-resize',
                background: 'transparent',
                touchAction: 'none',
                userSelect: 'none',
                // The line is invisible at rest — the panels usually
                // already carry a border on their own edge, so doubling
                // up reads as a stripe. Hover/drag reveals a clear
                // affordance.
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    width: visible ? '3px' : '0px',
                    transform: 'translateX(-50%)',
                    background: visible ? lineColor : 'transparent',
                    transition: 'width .12s ease, background .12s ease',
                    pointerEvents: 'none',
                    opacity: visible ? 1 : 0,
                },
            }}
        />
    );
}
